import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    Link,
    NavLink,
    useNavigate,
    createSearchParams,
} from 'react-router-dom';
import { FiSearch, FiShoppingCart, FiHeart, FiMenu, FiX, FiPhoneCall, FiTrendingUp, FiChevronRight, FiArrowLeft, FiSun, FiMoon } from 'react-icons/fi';
import { FaWhatsapp, FaFacebookF, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { useShop } from '../../context/ShopContext';
import { useTheme } from '../../context/ThemeContext';
import {
    RAHBER_LOGO_URL,
    RAHBER_LOGO_DARK_URL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_WHATSAPP_CHAT_URL,
    SOCIAL_LINKS,
} from '../../config/businessInfo';
import categoryService, { buildCategoryHierarchy } from '../../services/categoryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import TopMarqueeStrip from '../common/TopMarqueeStrip';
import './Header.css';

const QUICK_SEARCH_SUGGESTIONS = [
    { title: 'Air Conditioners', query: 'Air Conditioner', tag: 'Category' },
    { title: 'Inverter AC 1.5 Ton', query: 'Inverter AC', tag: 'Trending' },
    { title: 'Deep Freezers', query: 'Freezer', tag: 'Category' },
    { title: 'Refrigerators', query: 'Refrigerator', tag: 'Category' },
    { title: 'LED Smart TVs (4K)', query: 'TV', tag: 'Popular' },
    { title: 'Washing Machines', query: 'Washing Machine', tag: 'Category' },
    { title: 'Microwave Ovens', query: 'Microwave', tag: 'Category' },
    { title: 'Gree Inverter ACs', query: 'Gree', tag: 'Brand' },
    { title: 'Dawlance Appliances', query: 'Dawlance', tag: 'Brand' },
    { title: 'TCL Smart QLED', query: 'TCL', tag: 'Brand' },
];

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const headerRef = useRef(null);
    const searchWrapRef = useRef(null);
    const navigate = useNavigate();
    const { wishlistCount, cartItemCount } = useShop();
    const { isDark, toggleTheme } = useTheme();
    const [categories, setCategories] = useState([]);
    const [popoverParentId, setPopoverParentId] = useState(null);
    const [drawerParentId, setDrawerParentId] = useState(null);

    const hierarchy = useMemo(() => buildCategoryHierarchy(categories), [categories]);

    useEffect(() => {
        let isMounted = true;
        categoryService.getAll()
            .then((list) => {
                if (isMounted && Array.isArray(list)) {
                    setCategories(list);
                }
            })
            .catch(() => {});
        return () => {
            isMounted = false;
        };
    }, []);

    useRealtimeSync('categories', useCallback(() => {
        categoryService.getAll({}, { forceRefresh: true })
            .then((list) => {
                if (Array.isArray(list)) {
                    setCategories(list);
                }
            })
            .catch(() => {});
    }, []));

    useEffect(() => {
        if (!showSuggestions) {
            setPopoverParentId(null);
        }
    }, [showSuggestions]);

    useEffect(() => {
        if (!isMenuOpen) {
            setDrawerParentId(null);
        }
    }, [isMenuOpen]);

    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const setVar = () => {
            document.documentElement.style.setProperty(
                '--header-height',
                `${el.offsetHeight}px`,
            );
        };
        setVar();
        const ro = new ResizeObserver(setVar);
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, [isMenuOpen]);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    useEffect(() => {
        const handleScroll = () => {
            const y = window.scrollY;
            setIsScrolled(y > 10);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close suggestions on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const executeSearch = (query) => {
        const q = (query || searchQuery).trim();
        if (q) {
            navigate({
                pathname: '/products',
                search: `?${createSearchParams({ search: q }).toString()}`,
            });
        } else {
            navigate({ pathname: '/products' });
        }
        setSearchQuery('');
        setShowSuggestions(false);
        setIsMenuOpen(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        executeSearch(searchQuery);
    };

    const handleSuggestionClick = (query) => {
        executeSearch(query);
    };

    const filteredSuggestions = searchQuery.trim()
        ? QUICK_SEARCH_SUGGESTIONS.filter((s) =>
              s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              s.query.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : QUICK_SEARCH_SUGGESTIONS;

    const handleLogoClick = (e) => {
        if (window.location.pathname === '/') {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <header
            ref={headerRef}
            className={`riseup-header${isScrolled ? ' riseup-header--scrolled' : ''}`}
        >
            {/* Top Main Navigation Bar (Clean White, Rise Up Schools style) */}
            <div className="riseup-navbar">
                <div className="container riseup-navbar__inner">
                    {/* Brand Logo */}
                    <div className="riseup-logo">
                        <Link to="/" onClick={handleLogoClick} className="riseup-logo__link" aria-label="Rahber Corporation Home">
                            <img
                                src={isDark ? RAHBER_LOGO_DARK_URL : RAHBER_LOGO_URL}
                                alt="Rahber Corporation"
                                className="riseup-logo__img"
                                onError={(e) => {
                                    if (isDark && e.currentTarget.src !== RAHBER_LOGO_URL) {
                                        e.currentTarget.src = RAHBER_LOGO_URL;
                                        return;
                                    }
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.parentElement?.querySelector('.riseup-logo__text');
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                            <div className="riseup-logo__text" style={{ display: 'none' }}>
                                <span className="riseup-logo__brand">RAHBER</span>
                                <span className="riseup-logo__sub">CORPORATION</span>
                            </div>
                        </Link>
                    </div>

                    {/* Centered Desktop Menu */}
                    <nav className="riseup-nav-desktop" aria-label="Primary Navigation">
                        <ul className="riseup-nav-links">
                            <li>
                                <NavLink
                                    to="/"
                                    end
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    Home
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/about"
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    About
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/products"
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    Products
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/installment"
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    Buy on Installment
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/cash"
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    Buy on Cash
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/blogs"
                                    className={({ isActive }) =>
                                        isActive ? 'riseup-link riseup-link--active' : 'riseup-link'
                                    }
                                >
                                    Blogs
                                </NavLink>
                            </li>
                        </ul>
                    </nav>

                    {/* Right Action Suite (Search Pill + CTA Button + Icons) */}
                    <div className="riseup-actions">
                        <div className="riseup-search-container" ref={searchWrapRef}>
                            <form className="riseup-search-pill" onSubmit={handleSearch}>
                                <FiSearch className="riseup-search-icon" aria-hidden="true" />
                                <input
                                    type="text"
                                    placeholder="Search electronics..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setShowSuggestions(true);
                                    }}
                                    onFocus={() => setShowSuggestions(true)}
                                    aria-label="Search appliances"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className="search-clear-btn"
                                        onClick={() => setSearchQuery('')}
                                        aria-label="Clear search"
                                    >
                                        ×
                                    </button>
                                )}
                            </form>

                            {/* Rise Up Style Interactive Search Suggestions Popover */}
                            {showSuggestions && (
                                <div className="search-suggestions-popover">
                                    <div className="suggestions-header">
                                        <span className="suggestions-title">
                                            <FiTrendingUp /> Quick Suggestions
                                        </span>
                                        <span className="suggestions-sub">Popular appliances &amp; brands</span>
                                    </div>
                                    <ul className="suggestions-list">
                                        {filteredSuggestions.slice(0, 6).map((item, idx) => (
                                            <li key={idx} className="suggestion-item">
                                                <button
                                                    type="button"
                                                    className="suggestion-btn"
                                                    onClick={() => handleSuggestionClick(item.query)}
                                                >
                                                    <div className="suggestion-left">
                                                        <FiSearch className="suggestion-icon" />
                                                        <span className="suggestion-text">{item.title}</span>
                                                    </div>
                                                    <span className="suggestion-tag">{item.tag}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="suggestions-categories-strip">
                                        {popoverParentId && hierarchy.getCategoryById(popoverParentId) ? (
                                            <>
                                                <div className="categories-strip-header-row">
                                                    <button
                                                        type="button"
                                                        className="categories-strip-back"
                                                        onClick={() => {
                                                            const curr = hierarchy.getCategoryById(popoverParentId);
                                                            setPopoverParentId(curr?.parent_id || null);
                                                        }}
                                                    >
                                                        <FiArrowLeft aria-hidden="true" />
                                                        <span>Main Categories</span>
                                                    </button>
                                                    <span className="categories-strip-title categories-strip-title--inline">
                                                        {hierarchy.getCategoryById(popoverParentId).name}
                                                    </span>
                                                </div>
                                                <div className="categories-strip-chips">
                                                    {hierarchy.getSubcategories(popoverParentId).map((sub) => (
                                                        <button
                                                            key={sub._id}
                                                            type="button"
                                                            className="category-mini-chip"
                                                            onClick={() => {
                                                                if (hierarchy.hasSubcategories(sub._id)) {
                                                                    setPopoverParentId(String(sub._id));
                                                                } else {
                                                                    navigate(`/products?category=${encodeURIComponent(sub._id)}`);
                                                                    setShowSuggestions(false);
                                                                }
                                                            }}
                                                        >
                                                            {sub.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="categories-strip-title">Browse By Category:</span>
                                                <div className="categories-strip-chips">
                                                    {hierarchy.parentCategories.map((cat) => (
                                                        <button
                                                            key={cat._id}
                                                            type="button"
                                                            className="category-mini-chip"
                                                            onClick={() => {
                                                                if (hierarchy.hasSubcategories(cat._id)) {
                                                                    setPopoverParentId(String(cat._id));
                                                                } else {
                                                                    navigate(`/products?category=${encodeURIComponent(cat._id)}`);
                                                                    setShowSuggestions(false);
                                                                }
                                                            }}
                                                        >
                                                            {cat.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <Link to="/contact" className="riseup-cta-btn" title="Contact Rahber Corporation">
                            <FiPhoneCall className="riseup-cta-btn-icon" aria-hidden="true" />
                            <span>Contact Us</span>
                        </Link>

                        <div className="riseup-icon-group">
                            <button
                                type="button"
                                className={`riseup-icon-btn riseup-theme-toggle ${isDark ? 'is-dark' : ''}`}
                                onClick={toggleTheme}
                                title={isDark ? 'Switch to Theme 1: Showroom Light' : 'Switch to Theme 2: Marquee Royal Blue'}
                                aria-label={isDark ? 'Switch to Theme 1: Showroom Light' : 'Switch to Theme 2: Marquee Royal Blue'}
                            >
                                {isDark ? (
                                    <FiSun className="riseup-theme-icon riseup-theme-icon--sun" />
                                ) : (
                                    <FiMoon className="riseup-theme-icon riseup-theme-icon--moon" />
                                )}
                            </button>
                            <Link
                                to="/wishlist"
                                className="riseup-icon-btn"
                                title="Wishlist"
                                aria-label="Saved Wishlist"
                            >
                                <FiHeart />
                                {wishlistCount > 0 && (
                                    <span className="riseup-badge">{wishlistCount}</span>
                                )}
                            </Link>
                            <Link
                                to="/cart"
                                className="riseup-icon-btn riseup-icon-btn--cart"
                                title="Shopping Cart"
                                aria-label="Shopping Cart"
                            >
                                <FiShoppingCart />
                                {cartItemCount > 0 && (
                                    <span className="riseup-badge">{cartItemCount}</span>
                                )}
                            </Link>
                        </div>

                        <button
                            className="riseup-mobile-toggle"
                            onClick={toggleMenu}
                            aria-label="Toggle navigation menu"
                            aria-expanded={isMenuOpen}
                        >
                            {isMenuOpen ? <FiX /> : <FiMenu />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Continuous Marquee Notice Bar (Old original under navbar) */}
            <TopMarqueeStrip />

            {/* RISE UP SCHOOLS LUXURY FULL-FEATURED MOBILE DRAWER PORTAL */}
            {isMenuOpen && typeof document !== 'undefined' && createPortal(
                <div className="riseup-drawer-portal" role="dialog" aria-modal="true" aria-label="Navigation Menu">
                    {/* Backdrop */}
                    <div
                        className="riseup-drawer-backdrop"
                        onClick={() => setIsMenuOpen(false)}
                        aria-hidden="true"
                    />

                    {/* Sliding Drawer Panel */}
                    <div className="riseup-drawer-panel">
                        {/* Drawer Header */}
                        <div className="riseup-drawer-header">
                            <div className="riseup-drawer-brand">
                                <img
                                    src={isDark ? RAHBER_LOGO_DARK_URL : RAHBER_LOGO_URL}
                                    alt="Rahber Corporation"
                                    className="riseup-drawer-logo-img"
                                    onError={(e) => {
                                        if (isDark && e.currentTarget.src !== RAHBER_LOGO_URL) {
                                            e.currentTarget.src = RAHBER_LOGO_URL;
                                        }
                                    }}
                                />
                            </div>

                            <div className="riseup-drawer-top-actions">
                                <a
                                    href={`tel:${BUSINESS_PHONE_MAIN[0].raw}`}
                                    className="riseup-drawer-contact-pill"
                                >
                                    Call Now
                                </a>
                                <button
                                    type="button"
                                    className="riseup-drawer-close-btn"
                                    onClick={() => setIsMenuOpen(false)}
                                    aria-label="Close menu"
                                    title="Close navigation menu"
                                >
                                    <FiX />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Body */}
                        <div className="riseup-drawer-scroll-body">
                            {/* Search Form inside drawer */}
                            <form className="riseup-drawer-search-form" onSubmit={handleSearch}>
                                <FiSearch style={{ color: '#94A3B8', fontSize: '1.1rem', flexShrink: 0 }} />
                                <input
                                    type="text"
                                    placeholder="Search appliances, ACs, TVs, brands..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </form>

                            {/* Main Navigation Section */}
                            <div className="riseup-drawer-section">
                                <span className="riseup-drawer-section-title">Store Pages &amp; Catalog</span>
                                <nav className="riseup-drawer-nav">
                                    <NavLink
                                        to="/"
                                        end
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Home</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/about"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>About Us</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/products"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Products &amp; Catalog</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/installment"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Buy on Installment</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/cash"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Buy on Cash (Instant Discount)</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/blogs"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Blogs &amp; Buying Guides</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                    <NavLink
                                        to="/contact"
                                        className={({ isActive }) =>
                                            `riseup-drawer-nav-item ${isActive ? 'is-active' : ''}`
                                        }
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span>Contact &amp; Showroom</span>
                                        <FiChevronRight className="riseup-drawer-chevron" />
                                    </NavLink>
                                </nav>
                            </div>

                            {/* Hierarchical Appliance Categories */}
                            <div className="riseup-drawer-section">
                                {drawerParentId && hierarchy.getCategoryById(drawerParentId) ? (
                                    <>
                                        <div className="riseup-drawer-cat-header">
                                            <span className="riseup-drawer-section-title">
                                                {hierarchy.getCategoryById(drawerParentId).name}
                                            </span>
                                            <button
                                                type="button"
                                                className="riseup-drawer-back-cat-btn"
                                                onClick={() => {
                                                    const curr = hierarchy.getCategoryById(drawerParentId);
                                                    setDrawerParentId(curr?.parent_id || null);
                                                }}
                                            >
                                                <FiArrowLeft aria-hidden="true" />
                                                <span>Main Categories</span>
                                            </button>
                                        </div>
                                        <nav className="riseup-drawer-nav">
                                            {hierarchy.getSubcategories(drawerParentId).map((sub) => {
                                                const nestedCount = hierarchy.getSubcategories(sub._id).length;
                                                if (nestedCount > 0) {
                                                    return (
                                                        <button
                                                            key={sub._id}
                                                            type="button"
                                                            className="riseup-drawer-nav-item riseup-drawer-cat-btn"
                                                            onClick={() => setDrawerParentId(String(sub._id))}
                                                        >
                                                            <span className="riseup-drawer-bullet-item">
                                                                <span className="drawer-bullet-orange" />
                                                                <span>{sub.name}</span>
                                                            </span>
                                                            <span className="riseup-drawer-cat-right">
                                                                <span className="riseup-drawer-sub-badge">{nestedCount}</span>
                                                                <FiChevronRight className="riseup-drawer-chevron" />
                                                            </span>
                                                        </button>
                                                    );
                                                }
                                                return (
                                                    <Link
                                                        key={sub._id}
                                                        to={`/products?category=${encodeURIComponent(sub._id)}`}
                                                        className="riseup-drawer-nav-item"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="riseup-drawer-bullet-item">
                                                            <span className="drawer-bullet-orange" />
                                                            <span>{sub.name}</span>
                                                        </span>
                                                        <FiChevronRight className="riseup-drawer-chevron" />
                                                    </Link>
                                                );
                                            })}
                                        </nav>
                                    </>
                                ) : (
                                    <>
                                        <span className="riseup-drawer-section-title">Browse Categories</span>
                                        <nav className="riseup-drawer-nav">
                                            {hierarchy.parentCategories.map((cat) => {
                                                const subCount = hierarchy.getSubcategories(cat._id).length;
                                                if (subCount > 0) {
                                                    return (
                                                        <button
                                                            key={cat._id}
                                                            type="button"
                                                            className="riseup-drawer-nav-item riseup-drawer-cat-btn"
                                                            onClick={() => setDrawerParentId(String(cat._id))}
                                                        >
                                                            <span className="riseup-drawer-bullet-item">
                                                                <span className="drawer-bullet-orange" />
                                                                <span>{cat.name}</span>
                                                            </span>
                                                            <span className="riseup-drawer-cat-right">
                                                                <span className="riseup-drawer-sub-badge">{subCount}</span>
                                                                <FiChevronRight className="riseup-drawer-chevron" />
                                                            </span>
                                                        </button>
                                                    );
                                                }
                                                return (
                                                    <Link
                                                        key={cat._id}
                                                        to={`/products?category=${encodeURIComponent(cat._id)}`}
                                                        className="riseup-drawer-nav-item"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="riseup-drawer-bullet-item">
                                                            <span className="drawer-bullet-orange" />
                                                            <span>{cat.name}</span>
                                                        </span>
                                                        <FiChevronRight className="riseup-drawer-chevron" />
                                                    </Link>
                                                );
                                            })}
                                        </nav>
                                    </>
                                )}
                            </div>

                            {/* Quick Action Buttons Group */}
                            <div className="riseup-drawer-buttons-group">
                                <Link
                                    to="/installment"
                                    className="drawer-action-btn drawer-action-btn--primary"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <span>Calculate Monthly Installment</span>
                                </Link>
                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="drawer-action-btn drawer-action-btn--whatsapp"
                                >
                                    <FaWhatsapp className="drawer-whatsapp-icon" />
                                    <span>Chat on WhatsApp</span>
                                </a>
                                <a
                                    href={`tel:${BUSINESS_PHONE_MAIN[0].raw}`}
                                    className="drawer-action-btn drawer-action-btn--helpline"
                                >
                                    <FiPhoneCall />
                                    <span>Call Helpline ({BUSINESS_PHONE_MAIN[0].display})</span>
                                </a>
                            </div>

                            {/* Official Social Links in Drawer (Facebook, Instagram, YouTube, TikTok) */}
                            <div className="riseup-drawer-social-row">
                                <a
                                    href={SOCIAL_LINKS.find((s) => s.id === 'facebook')?.href || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="drawer-social-circle drawer-social-circle--fb"
                                    title="Facebook"
                                    aria-label="Facebook"
                                >
                                    <FaFacebookF />
                                </a>
                                <a
                                    href={SOCIAL_LINKS.find((s) => s.id === 'instagram')?.href || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="drawer-social-circle drawer-social-circle--ig"
                                    title="Instagram"
                                    aria-label="Instagram"
                                >
                                    <FaInstagram />
                                </a>
                                <a
                                    href={SOCIAL_LINKS.find((s) => s.id === 'youtube')?.href || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="drawer-social-circle drawer-social-circle--yt"
                                    title="YouTube"
                                    aria-label="YouTube"
                                >
                                    <FaYoutube />
                                </a>
                                <a
                                    href={SOCIAL_LINKS.find((s) => s.id === 'tiktok')?.href || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="drawer-social-circle drawer-social-circle--tt"
                                    title="TikTok"
                                    aria-label="TikTok"
                                >
                                    <FaTiktok />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </header>
    );
};

export default Header;
